import { FC, useState, MouseEvent } from 'react';
import ConfirmationDialog from 'src/components/DialogBox/ConfirmationDialog/ConfirmationDialog';
import ProductionMappingViewDialog from 'src/components/DialogBox/ProductionMappingViewDialog';
import ProductionMappingEditDialog from 'src/components/DialogBox/ProductionMappingEditDialog';
import {
  actionButtons,
  productionMappingDialogTitles,
  productionMappingDialogSubtitles,
} from 'src/utils/properties.utils';
import { capitalizeFirstLetter } from 'src/utils/text.utils';
import { ProductionMapping } from 'src/models/Management.interface';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import {
  deleteProductionMapping,
  getProductionMappingById,
} from 'src/services/productionMapping.service';
import { getApiErrorMessage } from 'src/utils/errorHandler';
import {
  ActionMenuButton,
  ActionMenuPopover,
  MenuItemsWrapper,
  MenuItemWithIcon,
} from 'src/components/StyledComponents/StyledActionMenu';

const ProductionMappingActionMenuButton: FC<{
  mapping: ProductionMapping;
  onUpdate: () => void;
}> = ({ mapping, onUpdate }) => {
  const { showSnackbar } = StyledSnackbar();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState<string | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedMapping, setSelectedMapping] =
    useState<ProductionMapping | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClose = () => setAnchorEl(null);

  const handleClick = (event: MouseEvent<HTMLElement>) =>
    setAnchorEl(event.currentTarget);

  const handleDialogOpen = (type: string) => {
    setDialogType(type);
    setDialogOpen(true);
    handleClose();
  };

  const handleView = async () => {
    try {
      const fullMapping = await getProductionMappingById(mapping.id);
      setSelectedMapping(fullMapping);
      setViewDialogOpen(true);
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    }
    handleClose();
  };

  const handleViewDialogClose = () => {
    setViewDialogOpen(false);
    setSelectedMapping(null);
  };

  const handleEdit = async () => {
    try {
      const fullMapping = await getProductionMappingById(mapping.id);
      setSelectedMapping(fullMapping);
      setEditDialogOpen(true);
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    }
    handleClose();
  };

  const handleEditDialogClose = () => {
    setEditDialogOpen(false);
    setSelectedMapping(null);
  };

  const handleConfirm = async () => {
    try {
      if (dialogType === actionButtons.DELETE) {
        const response = await deleteProductionMapping(mapping.id);
        showSnackbar(response.message, 'success');
        onUpdate();
      }
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    } finally {
      setDialogOpen(false);
      setDialogType(null);
    }
  };

  return (
    <>
      <ActionMenuButton
        onClick={handleClick}
        component="i"
        className="bx bx-dots-horizontal-rounded"
      />
      <ActionMenuPopover open={open} anchorEl={anchorEl} onClose={handleClose}>
        <MenuItemsWrapper>
          <MenuItemWithIcon
            onClick={handleView}
            iconClassName="bx bx-show"
            text="View"
          />

          <MenuItemWithIcon
            onClick={handleEdit}
            iconClassName="bx bx-edit"
            text="Edit"
          />

          <MenuItemWithIcon
            onClick={() => handleDialogOpen(actionButtons.DELETE)}
            iconClassName="bx bx-trash"
            text="Delete"
          />
        </MenuItemsWrapper>
      </ActionMenuPopover>

      <ProductionMappingViewDialog
        open={viewDialogOpen}
        handleClose={handleViewDialogClose}
        mapping={selectedMapping}
        title={productionMappingDialogTitles.VIEW}
        subtitle={productionMappingDialogSubtitles.VIEW}
      />

      <ProductionMappingEditDialog
        open={editDialogOpen}
        mapping={selectedMapping}
        onClose={handleEditDialogClose}
        onSuccess={onUpdate}
      />

      <ConfirmationDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onConfirm={handleConfirm}
        title={productionMappingDialogTitles.DELETE}
        subtitle={`Are you sure you want to delete the production mapping for "${mapping.productName}"? This action cannot be undone.`}
        confirmText={capitalizeFirstLetter(dialogType || '')}
        confirmButtonColor="var(--error)"
        alertIconColor="var(--error)"
      />
    </>
  );
};

export default ProductionMappingActionMenuButton;
